"use strict";
/**
 * Copyright © 2019 - Present, Vamstar Limited
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or
 * without modification, are not permitted.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.CurrencyUtils = void 0;
const constants_1 = require("../constants");
const fox_node_logger_1 = require("@vamstar/fox-node-logger");
const iso_country_currency_1 = require("iso-country-currency");
const types_1 = require("./types");
const constants_2 = require("./constants");
class CurrencyUtils {
    /**
     * Checks whether a number is valid
     * @param num - any number
     * @returns boolean
     */
    static isValidNumber(num) {
        return Number.isFinite(num) && num > 0;
    }
    /**
     * Gets the estimated value of a lot
     * @param data - ISearchResultUnion
     * @returns string
     */
    static getEstimatedLotValue(data) {
        const { lotValueEstimatedEur } = data;
        if (lotValueEstimatedEur) {
            return `${this.__getAmountLabel(lotValueEstimatedEur)}`;
        }
        return '';
    }
    /**
     * Gets the estimated value of a contract
     * @param data - ISearchResultUnion
     * @returns string
     */
    static getEstimatedContractValue(data) {
        const { contractValueEstimatedEur } = data;
        if (contractValueEstimatedEur) {
            return `${this.__getAmountLabel(contractValueEstimatedEur)}`;
        }
        return '';
    }
    /**
     * Gets the original lot value at the source
     * @param data - ISearchResultUnion
     * @returns string
     */
    static getLotValueOriginal(data) {
        const { lotValueOriginal, lotValueCurrency } = data;
        if (lotValueOriginal) {
            return `${lotValueOriginal} ${lotValueCurrency}`;
        }
        return '';
    }
    /**
     * Gets currency symbol
     * @param currencyCode - string
     * @returns string
     */
    static getCurrencySymbol(currencyCode) {
        const currencies = (0, iso_country_currency_1.getAllISOCodes)();
        const validCurrency = currencies.find(currency => currency.currency.toUpperCase() === currencyCode.toUpperCase());
        if (validCurrency) {
            return validCurrency.symbol;
        }
        return '';
    }
    /**
     * Gets currency
     * @param country - string
     * @returns string
     */
    static getCurrencyOfCountry(country) {
        const currencies = (0, iso_country_currency_1.getAllISOCodes)();
        const validCurrency = currencies.find(currency => currency.iso.toLowerCase() === country.toLowerCase());
        if (validCurrency) {
            return validCurrency;
        }
        return null;
    }
    /**
     * Check if currency value valid
     * @param value - string
     * @returns boolean
     * @description Strings with any special charachter except decimal is not valid.
     */
    static isValidCurrencyString(value) {
        return constants_1.CURRENCY_VALUE_REGEX.test(value);
    }
    static getAllCurrencies() {
        return (0, iso_country_currency_1.getAllISOCodes)();
    }
}
exports.CurrencyUtils = CurrencyUtils;
/**
 * Formats the currency to human readable numbers
 * @param amount - string | number | undefined
 * @returns string
 */
CurrencyUtils.__getAmountLabel = (amount) => {
    const parsedAmount = Number(amount);
    let amountStr = '';
    if (CurrencyUtils.isValidNumber(parsedAmount)) {
        try {
            amountStr = `${parsedAmount.toFixed(2)} EUR`;
        }
        catch (err) {
            fox_node_logger_1.log.debug(err);
        }
    }
    return amountStr;
};
/**
 *
 * @param val
 * @returns [number, string]
 * @description splits the currency value from a string that includes the currency symbol or name
 */
CurrencyUtils.parseAmountFromString = (val) => {
    const number = typeof val === 'number' ? val : Number(val.split(/\D+/).filter(Boolean)[0]);
    const string = (typeof val === 'string' ? val.replace(/\d+/g, '') : '').trim();
    return [number, string];
};
/**
 * @param value - currecny value {number}
 * @param currencyCode - a string representation of the currency code
 * @default 'EUR'
 * @param locale - a string representation of the locale
 * @default 'en-us'
 * @description a string representation of the currency formatted according to the locale specified
 */
CurrencyUtils.format = (amount, currencyCode = constants_2.DEFAULT_CURRENCY_CODE, locale = constants_2.DEFAULT_CURRENCY_LOCALE) => {
    const formattedAmount = new Intl.NumberFormat(locale, { minimumFractionDigits: 2 }).format(amount);
    return `${types_1.CurrencySymbolsMap[currencyCode]}${formattedAmount}`;
};
//# sourceMappingURL=data:application/json;base64,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